/**
 * VerifyEmail — /verifica-email?token=XXX
 *
 * Landing page reached from the verification email we send after signup.
 * Consumes the token via POST /auth/verify-email; on first success fires
 * the Meta Pixel `CompleteRegistration` event (moved here from signup —
 * see v1.36 changelog). Auto-redirects to the client dashboard after ~1s
 * so the celebration screen doesn't linger.
 *
 * Three visual states:
 *   - verifying: spinner (first render, network call in flight)
 *   - success:   confetti + auto-redirect
 *   - error:     inline explanation + "resend" affordance
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, AlertTriangle, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, resendVerification, apiError } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  // Prevent React StrictMode double-invoke from burning the token twice.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Link non valido: manca il token.");
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        setStatus("success");
        // Give the user a beat to see the checkmark before redirecting.
        setTimeout(() => navigate("/dashboard"), 1200);
      } catch (e) {
        setStatus("error");
        setErrorMsg(apiError(e) || "Link non valido o scaduto.");
      }
    })();
  }, [params, verifyEmail, apiError, navigate]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) {
      toast.error("Inserisci la tua email");
      return;
    }
    setResending(true);
    try {
      await resendVerification(resendEmail.trim());
      toast.success("Ti abbiamo rimandato il link (se l'email è valida)");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="verify-email-page"
      className="min-h-screen grid place-items-center bg-[var(--mp-dash-bg,#faf9f6)] px-4 py-12"
    >
      <div className="w-full max-w-md rounded-3xl border-2 border-[var(--mp-ink,#0a0a0a)] bg-white shadow-brutal-sm p-8 sm:p-10 text-center">
        {status === "verifying" && (
          <div data-testid="verify-verifying">
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-[var(--mp-ink,#0a0a0a)]" />
            <h1 className="font-display text-2xl mt-4">Stiamo attivando il tuo account…</h1>
            <p className="text-sm text-[var(--mp-muted,#666)] mt-2">
              Ci vuole solo un secondo.
            </p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <div className="h-14 w-14 mx-auto rounded-full bg-emerald-500 grid place-items-center">
              <CheckCircle2 className="h-8 w-8 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-3xl mt-4 leading-tight">
              Email confermata!
            </h1>
            <p className="text-sm text-[var(--mp-muted,#666)] mt-2 leading-relaxed">
              Il tuo account è attivo. Ti stiamo portando nella tua dashboard…
            </p>
            <div className="mt-6">
              <Loader2 className="h-4 w-4 mx-auto animate-spin text-[var(--mp-muted,#666)]" />
            </div>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <div className="h-14 w-14 mx-auto rounded-full bg-red-500 grid place-items-center">
              <AlertTriangle className="h-7 w-7 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-2xl mt-4">
              Link non valido
            </h1>
            <p className="text-sm text-[var(--mp-muted,#666)] mt-2 leading-relaxed">
              {errorMsg}
            </p>
            <form
              onSubmit={handleResend}
              className="mt-6 space-y-3 text-left"
              data-testid="verify-resend-form"
            >
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--mp-muted,#666)]">
                  Rimandami il link
                </label>
                <Input
                  data-testid="verify-resend-email"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="La tua email"
                  className="mt-1 rounded-full border-[var(--mp-ink,#0a0a0a)]"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="verify-resend-btn"
                disabled={resending}
                className="w-full h-11 rounded-full bg-[var(--mp-ink,#0a0a0a)] hover:bg-black text-white"
              >
                {resending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <MailCheck className="h-4 w-4" /> Rimandami il link
                  </span>
                )}
              </Button>
            </form>
            <Link
              to="/cliente/login"
              className="inline-block mt-6 text-xs text-[var(--mp-muted,#666)] hover:text-[var(--mp-ink,#0a0a0a)] underline"
            >
              Torna al login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
